// CrystalVault — Wallet Connection (HyperEVM)

import { NETWORK, USDC_ADDRESS, USDC_DECIMALS, ERC20_ABI } from './config.js';

const STORAGE_KEY = "cv_wallet_address";
let _address = localStorage.getItem(STORAGE_KEY) || null;
let _provider = null;
let _signer = null;
let _viewOnly = false;

// Read-only "view any address" mode: `?view=0x…` renders that wallet's positions
// without connecting a wallet (all position data is public on-chain). No signer is
// set, so no actions are possible — purely for previewing / testing / support /
// checking a vault from any device. Takes precedence over a stored/connected addr.
(function _initViewMode() {
  try {
    const v = new URLSearchParams(location.search).get("view");
    if (v && /^0x[0-9a-fA-F]{40}$/.test(v)) { _address = v; _viewOnly = true; }
  } catch {}
})();

export function isConnected() { return !!_address; }
export function isViewOnly() { return _viewOnly; }
export function getAddress() { return _address; }
export function getSigner() { return _signer; }
export function getProvider() { return _provider; }

// Cap JSON-RPC batches at 6 calls. ethers v6 default is 100, but HL's
// public RPC rejects batches >20 with -32010 ("Exceeded max limit of 20").
// Per-vault Promise.all fires 6 reads in one tick, so cap=6 means each
// vault's reads land as one batched HTTP request (good — fewer round-trips),
// but distinct vaults' reads stay in distinct batches (good — stay under
// HL's 20-cap even with 6 vaults × 8 calls = 48 firing concurrently from
// the parallelized Overview render).
//
// staticNetwork=true with the known chainId skips ethers' eth_chainId
// auto-detect (one fewer round-trip on every provider use, and avoids
// "JsonRpcProvider failed to detect network" console noise observed
// 2026-05-08 right after the parallel-loop change).
//
// Memoize so we reuse the same provider/connection state across calls.
let _readProvider = null;
export function getReadProvider() {
  if (_readProvider) return _readProvider;
  _readProvider = new ethers.JsonRpcProvider(
    NETWORK.rpcUrl,
    { chainId: NETWORK.chainId, name: NETWORK.chainName || "hyperevm" },
    { batchMaxCount: 6, staticNetwork: true },
  );
  return _readProvider;
}

/**
 * Silent reconnect — uses eth_accounts (NOT eth_requestAccounts) so MetaMask
 * never pops up. Returns the address if MetaMask still has us authorized,
 * otherwise null. Call this on page load; only call connect() when the user
 * explicitly clicks "Connect Wallet".
 */
export async function tryReconnect() {
  if (_viewOnly) return _address; // view-only preview wins over a connected wallet
  if (!window.ethereum) return null;
  try {
    const accounts = await window.ethereum.request({ method: "eth_accounts" });
    if (!accounts || accounts.length === 0) {
      // MetaMask either locked, never authorized this site, or revoked us
      // Clear stale localStorage so we don't keep trying
      localStorage.removeItem(STORAGE_KEY);
      _address = null;
      return null;
    }
    _address = accounts[0];
    localStorage.setItem(STORAGE_KEY, _address);

    // Set up provider + signer (no prompts)
    _provider = new ethers.BrowserProvider(window.ethereum);
    _signer = await _provider.getSigner();

    // Wire up account-change + chain-change listeners
    _attachListeners();
    return _address;
  } catch (e) {
    console.warn("silent reconnect failed:", e);
    return null;
  }
}

function _attachListeners() {
  if (!window.ethereum) return;
  // Avoid double-binding by checking a sentinel
  if (window.ethereum._cvListenersAttached) return;
  window.ethereum._cvListenersAttached = true;

  window.ethereum.on("accountsChanged", (accs) => {
    if (accs.length === 0) { disconnect(); return; }
    _address = accs[0];
    localStorage.setItem(STORAGE_KEY, _address);
    window.dispatchEvent(new Event("wallet-changed"));
  });

  window.ethereum.on("chainChanged", (newChainHex) => {
    if (parseInt(newChainHex, 16) !== NETWORK.chainId) {
      window.dispatchEvent(new CustomEvent("wallet-wrong-chain", { detail: { chainId: parseInt(newChainHex, 16) } }));
    } else {
      window.dispatchEvent(new Event("wallet-changed"));
    }
  });
}

export async function connect() {
  if (!window.ethereum) {
    alert("Please install MetaMask");
    return null;
  }

  _viewOnly = false; // explicit connect exits read-only preview
  const accounts = await window.ethereum.request({ method: "eth_requestAccounts" });
  _address = accounts[0];

  // Switch to the active network if needed; auto-add if MetaMask doesn't know it
  const chainIdHex = await window.ethereum.request({ method: "eth_chainId" });
  if (parseInt(chainIdHex, 16) !== NETWORK.chainId) {
    try {
      await window.ethereum.request({
        method: "wallet_switchEthereumChain",
        params: [{ chainId: NETWORK.chainHex }],
      });
    } catch (e) {
      // 4902 = chain not added; offer to add it
      if (e.code === 4902) {
        try {
          await window.ethereum.request({
            method: "wallet_addEthereumChain",
            params: [{
              chainId: NETWORK.chainHex,
              chainName: NETWORK.chainName,
              nativeCurrency: NETWORK.nativeCurrency,
              rpcUrls: [NETWORK.rpcUrl],
              blockExplorerUrls: [NETWORK.explorer],
            }],
          });
        } catch (addErr) {
          alert(`Please switch your wallet to ${NETWORK.chainName} (chain ID ${NETWORK.chainId})`);
          return null;
        }
      } else {
        alert(`Please switch your wallet to ${NETWORK.chainName} (chain ID ${NETWORK.chainId})`);
        return null;
      }
    }
  }

  _provider = new ethers.BrowserProvider(window.ethereum);
  _signer = await _provider.getSigner();
  localStorage.setItem(STORAGE_KEY, _address);

  _attachListeners();

  window.dispatchEvent(new Event("wallet-changed"));
  return _address;
}

export function disconnect() {
  _address = null;
  _provider = null;
  _signer = null;
  localStorage.removeItem(STORAGE_KEY);
  window.dispatchEvent(new Event("wallet-changed"));
}

export async function getUsdcBalance() {
  if (!_address) return 0;
  try {
    const rp = getReadProvider();
    const usdc = new ethers.Contract(USDC_ADDRESS, ERC20_ABI, rp);
    const bal = await usdc.balanceOf(_address);
    return Number(bal) / 10 ** USDC_DECIMALS;
  } catch (e) {
    console.warn("USDC balance read failed:", e);
    return 0;
  }
}

// HYPE is HyperEVM's native gas token (18 decimals, chainId 999). Every
// deposit, approval, and withdrawal pays gas in HYPE — so a wallet with
// USDC but zero HYPE will get "insufficient funds for gas" from MetaMask
// on the first send. We surface this on the Deposit page so users can
// bridge HYPE from Hyperliquid Core BEFORE they try (and before they get
// confused). 18 decimals → divide by 1e18.
export async function getHypeBalance() {
  if (!_address) return 0;
  try {
    const rp = getReadProvider();
    const bal = await rp.getBalance(_address);
    return Number(bal) / 1e18;
  } catch (e) {
    console.warn("HYPE balance read failed:", e);
    return 0;
  }
}

export function shortAddr(addr) {
  if (!addr) return "";
  return addr.slice(0, 6) + "..." + addr.slice(-4);
}
